import React, { useState } from 'react';
import { ChevronRight, FileText, Mail, Ticket, Calendar, CreditCard } from 'lucide-react';
import { SyncRun, SyncSource, SOURCES, STATUS_DOT, runsBySource, relativeTime, formatDuration } from './types';
interface Props {
  onSelectRun: (runId: string) => void;
}
const SOURCE_ICONS: Record<SyncSource, React.FC<{
  size?: number;
  className?: string;
}>> = {
  confluence: FileText,
  gmail: Mail,
  jira: Ticket,
  gcal: Calendar,
  quickbooks: CreditCard
};
export function SyncIndex({
  onSelectRun
}: Props) {
  const [showOlder, setShowOlder] = useState<Record<string, boolean>>({});
  const grouped = runsBySource();
  return <>
      <header className="mb-8" data-id="element-2075">
        <h1 className="text-[20px] font-medium text-[#e8e4df] tracking-tight mb-1" data-id="element-2076">Sync History</h1>
        <p className="text-[#8a8680] text-[14px]" data-id="element-2077">Pipeline observability — latest Nango syncs, per source.</p>
      </header>

      {SOURCES.map(source => {
      const runs = grouped[source.id];
      const SourceIcon = SOURCE_ICONS[source.id];
      const visible = showOlder[source.id] ? runs : runs.slice(0, 3);
      const hasMore = runs.length > visible.length;
      return <section key={source.id} className="mb-10" data-id="element-2078">
            <div className="flex items-center justify-between mb-4" data-id="element-2079">
              <div className="flex items-center gap-3" data-id="element-2080">
                <div className="w-10 h-10 rounded-[12px] bg-[#252530] flex items-center justify-center" data-id="element-2081">
                  <SourceIcon size={18} className="text-[#8a8680]" data-id="element-2082" />
                </div>
                <div data-id="element-2083">
                  <h2 className="text-[15px] font-medium text-[#e8e4df]" data-id="element-2084">{source.label}</h2>
                  <p className="text-[12px] text-[#8a8680]/70" data-id="element-2085">
                    last sync · {runs[0] ? relativeTime(runs[0].startedAt) : 'never'} · {source.cadence}
                  </p>
                </div>
              </div>
              {runs[0] && <span className={`w-2 h-2 rounded-full ${STATUS_DOT[runs[0].status]}`} data-id="element-2086" />}
            </div>

            {runs.length === 0 ? <p className="text-[#8a8680]/50 text-[13px] pl-[52px]" data-id="element-2087">No syncs in the last 7 days</p> : <div className="flex flex-col gap-2" data-id="element-2088">
                {visible.map(run => <RunRow key={run.id} run={run} onClick={() => onSelectRun(run.id)} data-id="element-2089" />)}
              </div>}

            {hasMore && <button onClick={() => setShowOlder(prev => ({
          ...prev,
          [source.id]: true
        }))} className="mt-3 ml-[52px] text-[12px] text-[#8a8680] hover:text-[#e8e4df] transition-colors" data-id="element-2090">
                Show older
              </button>}
          </section>;
    })}
    </>;
}
function RunRow({
  run,
  onClick
}: {
  run: SyncRun;
  onClick: () => void;
}) {
  const {
    counts,
    status,
    startedAt,
    durationMs
  } = run;
  return <button onClick={onClick} className="w-full text-left bg-[#1c1c22] rounded-[12px] px-4 py-3 hover:bg-[#1e1e25] transition-colors" data-id="element-2091">
      <div className="flex items-center gap-3" data-id="element-2092">
        <span className={`w-2 h-2 rounded-full ${STATUS_DOT[status]} shrink-0`} data-id="element-2093" />
        <span className="text-[13px] text-[#e8e4df] font-medium w-20 shrink-0" data-id="element-2094">{relativeTime(startedAt)}</span>
        <div className="flex items-center gap-2 flex-1 min-w-0" data-id="element-2095">
          {counts.new > 0 && <Pill text={`+${counts.new} new`} tone="teal" data-id="element-2096" />}
          {counts.updated > 0 && <Pill text={`~${counts.updated} updated`} tone="muted" data-id="element-2097" />}
          {counts.failed > 0 && <Pill text={`!${counts.failed} failed`} tone="red" data-id="element-2098" />}
          {counts.new === 0 && counts.updated === 0 && counts.failed === 0 && status !== 'running' && <span className="text-[12px] text-[#8a8680]/40" data-id="element-2099">no changes</span>}
          {status === 'running' && <span className="text-[12px] text-[#e8a84c]" data-id="element-2100">running…</span>}
        </div>
        <span className="text-[12px] text-[#8a8680]/40 shrink-0" data-id="element-2101">{formatDuration(durationMs)}</span>
        <ChevronRight size={14} className="text-[#8a8680]/30 shrink-0" data-id="element-2102" />
      </div>
    </button>;
}
function Pill({
  text,
  tone
}: {
  text: string;
  tone: 'teal' | 'muted' | 'red';
}) {
  const classes = tone === 'teal' ? 'bg-[#4a9a9a]/20 text-[#4a9a9a]' : tone === 'red' ? 'bg-[#f47067]/15 text-[#f47067]' : 'bg-[#252530] text-[#8a8680]';
  return <span className={`text-[11px] px-2 py-0.5 rounded-full ${classes}`} data-id="element-2103">{text}</span>;
}